import { useCallback, useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api';
import { EmploymentTypeSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { HrCandidatePanel } from './HrCandidatePanel';
import {
  CANDIDATE_SOURCES,
  MANPOWER_REQUEST_TYPES,
  recruitmentStatusVariant,
  resolvePipelineStages,
  stageColumnStyles,
  stageLabel,
} from './recruitmentUtils';
import type {
  HrCandidateSummary,
  HrJobPosting,
  HrJobRequisition,
  HrManpowerRequest,
  HrRecruitmentAnalytics,
  HrRecruitmentDashboard,
  HrRecruitmentPipeline,
  HrRecruitmentWorkQueue,
} from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

type Tab = 'dashboard' | 'workqueue' | 'planning' | 'requisitions' | 'jobs' | 'pipeline';

const TABS: { id: Tab; label: string; hint: string }[] = [
  { id: 'dashboard', label: 'Dashboard', hint: 'Hiring metrics, funnel, and analytics' },
  { id: 'workqueue', label: 'Work queue', hint: 'Pending interviews, offers, documents, confirmations' },
  { id: 'planning', label: 'Workforce planning', hint: 'Headcount requests' },
  { id: 'requisitions', label: 'Requisitions', hint: 'Approved positions to fill' },
  { id: 'jobs', label: 'Job postings', hint: 'Open roles candidates apply to' },
  { id: 'pipeline', label: 'Pipeline', hint: 'Candidate kanban by stage' },
];

export function HrRecruitmentPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<HrRecruitmentDashboard | null>(null);
  const [pipeline, setPipeline] = useState<HrRecruitmentPipeline | null>(null);
  const [jobs, setJobs] = useState<HrJobPosting[]>([]);
  const [requisitions, setRequisitions] = useState<HrJobRequisition[]>([]);
  const [manpower, setManpower] = useState<HrManpowerRequest[]>([]);
  const [analytics, setAnalytics] = useState<HrRecruitmentAnalytics | null>(null);
  const [workQueue, setWorkQueue] = useState<HrRecruitmentWorkQueue | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<HrCandidateSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [requisitionModalOpen, setRequisitionModalOpen] = useState(false);
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [manpowerModalOpen, setManpowerModalOpen] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const [dash, stats] = await Promise.all([hrApi.recruitmentDashboard(), hrApi.recruitmentAnalytics()]);
      setDashboard(dash);
      setAnalytics(stats);
    } catch (err) {
      showApiError(err, 'Failed to load recruitment dashboard');
    }
  }, []);

  const loadWorkQueue = useCallback(async () => {
    try {
      setWorkQueue(await hrApi.recruitmentWorkQueue());
    } catch (err) {
      showApiError(err, 'Failed to load work queue');
    }
  }, []);

  const loadPipeline = useCallback(async (search?: string) => {
    try {
      setPipeline(await hrApi.recruitmentPipeline(search?.trim() || undefined));
    } catch (err) {
      showApiError(err, 'Failed to load pipeline');
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      setJobs(await hrApi.listJobs());
    } catch (err) {
      showApiError(err, 'Failed to load job postings');
    }
  }, []);

  const loadRequisitions = useCallback(async () => {
    try {
      setRequisitions(await hrApi.listRequisitions());
    } catch (err) {
      showApiError(err, 'Failed to load requisitions');
    }
  }, []);

  const loadManpower = useCallback(async () => {
    try {
      setManpower(await hrApi.listManpowerRequests());
    } catch (err) {
      showApiError(err, 'Failed to load manpower requests');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadDashboard(),
      loadWorkQueue(),
      loadPipeline(),
      loadJobs(),
      loadRequisitions(),
      loadManpower(),
    ]);
    setLoading(false);
  }, [loadDashboard, loadWorkQueue, loadPipeline, loadJobs, loadRequisitions, loadManpower]);

  useEffect(() => {
    void load();
  }, [load]);

  const jobOptions = useMemo(
    () =>
      jobs.map((j) => ({
        value: String(j.id),
        label: `${j.title} (${j.statusCode})`,
      })),
    [jobs],
  );

  const requisitionColumns: Column<HrJobRequisition>[] = [
    { key: 'title', header: 'Position' },
    { key: 'location', header: 'Location', render: (r) => r.location ?? '—' },
    { key: 'employmentType', header: 'Employment', render: (r) => r.employmentType ?? '—' },
    { key: 'positionsCount', header: 'Headcount' },
    {
      key: 'statusCode',
      header: 'Status',
      render: (r) => <Badge variant={recruitmentStatusVariant(r.statusCode)}>{r.statusCode.replace(/_/g, ' ')}</Badge>,
    },
    {
      key: 'id',
      header: '',
      actions: true,
      render: (r) => (
        <TableRowActions>
          {r.statusCode === 'DRAFT' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await hrApi.submitRequisition(r.id);
                  notify.success('Submitted for approval');
                  await loadRequisitions();
                } catch (err) {
                  showApiError(err, 'Submit failed');
                }
              }}
            >
              Submit
            </Button>
          )}
          {r.statusCode === 'APPROVED' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await hrApi.createPostingFromRequisition(r.id, { statusCode: 'OPEN' });
                  notify.success('Job posting created');
                  await loadJobs();
                  setTab('jobs');
                } catch (err) {
                  showApiError(err, 'Failed to create job posting');
                }
              }}
            >
              Post job
            </Button>
          )}
          {r.statusCode === 'PENDING_APPROVAL' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await hrApi.approveRequisition(r.id);
                  notify.success('Requisition approved');
                  await loadRequisitions();
                } catch (err) {
                  showApiError(err, 'Approval failed');
                }
              }}
            >
              Approve
            </Button>
          )}
        </TableRowActions>
      ),
    },
  ];

  const jobColumns: Column<HrJobPosting>[] = [
    { key: 'title', header: 'Title' },
    { key: 'location', header: 'Location', render: (j) => j.location ?? '—' },
    { key: 'employmentType', header: 'Employment', render: (j) => j.employmentType ?? '—' },
    { key: 'positionsCount', header: 'Positions', render: (j) => j.positionsCount ?? 1 },
    {
      key: 'statusCode',
      header: 'Status',
      render: (j) => <Badge variant={recruitmentStatusVariant(j.statusCode)}>{j.statusCode}</Badge>,
    },
  ];

  const manpowerColumns: Column<HrManpowerRequest>[] = [
    { key: 'title', header: 'Title' },
    {
      key: 'requestType',
      header: 'Type',
      render: (m) => MANPOWER_REQUEST_TYPES.find((t) => t.value === m.requestType)?.label ?? m.requestType,
    },
    { key: 'positionsCount', header: 'Positions' },
    {
      key: 'statusCode',
      header: 'Status',
      render: (m) => <Badge variant={recruitmentStatusVariant(m.statusCode)}>{m.statusCode.replace(/_/g, ' ')}</Badge>,
    },
    {
      key: 'id',
      header: '',
      actions: true,
      render: (m) => (
        <TableRowActions>
          {m.statusCode === 'DRAFT' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await hrApi.submitManpowerRequest(m.id);
                  notify.success('Submitted for approval');
                  await loadManpower();
                } catch (err) {
                  showApiError(err, 'Submit failed');
                }
              }}
            >
              Submit
            </Button>
          )}
          {m.statusCode === 'PENDING_APPROVAL' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await hrApi.approveManpowerRequest(m.id);
                  notify.success('Manpower request approved');
                  await loadManpower();
                } catch (err) {
                  showApiError(err, 'Approval failed');
                }
              }}
            >
              Approve
            </Button>
          )}
        </TableRowActions>
      ),
    },
  ];

  const activeTab = TABS.find((t) => t.id === tab) ?? TABS[0];

  const openCandidateById = (candidateId?: number) => {
    if (!candidateId) return;
    const all = pipeline?.stages
      ? Object.values(pipeline.stages).flat()
      : [];
    const found = all.find((c) => c.id === candidateId);
    if (found) {
      setSelectedCandidate(found);
      setTab('pipeline');
      return;
    }
    setSelectedCandidate({
      id: candidateId,
      fullName: `Candidate #${candidateId}`,
      stageCode: 'APPLIED',
      email: undefined,
    });
    setTab('pipeline');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recruitment"
        description="Requisition → posting → pipeline → offer → onboarding → employee"
        breadcrumbs={[{ label: 'HR', to: '/hr' }, { label: 'Recruitment' }]}
        actions={
          <div className="flex flex-wrap gap-2">
            {tab === 'requisitions' && (
              <Button onClick={() => setRequisitionModalOpen(true)}>New requisition</Button>
            )}
            {tab === 'jobs' && <Button onClick={() => setJobModalOpen(true)}>New job posting</Button>}
            {tab === 'pipeline' && <Button onClick={() => setCandidateModalOpen(true)}>Add candidate</Button>}
            {tab === 'planning' && <Button onClick={() => setManpowerModalOpen(true)}>New manpower request</Button>}
            <Button variant="secondary" loading={loading} onClick={() => void load()}>
              Refresh
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setSelectedCandidate(null);
            }}
            className={cn(
              'rounded-t-lg px-4 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border border-b-0 border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="-mt-4 text-sm text-muted">{activeTab.hint}</p>

      {selectedCandidate ? (
        <HrCandidatePanel
          candidate={selectedCandidate}
          onRefresh={() => void load()}
          onClose={() => setSelectedCandidate(null)}
        />
      ) : (
        <>
          {tab === 'dashboard' && (
            <div className="space-y-6">
              {!dashboard && loading ? (
                <p className="text-sm text-muted">Loading dashboard…</p>
              ) : dashboard ? (
                <>
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Open hiring</h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <StatCard title="Open positions" value={dashboard.openPositions} />
                      <StatCard title="Candidates in pipeline" value={dashboard.candidatesInPipeline} />
                      <StatCard title="Interviews scheduled" value={dashboard.interviewsScheduled} />
                      <StatCard title="Offers pending" value={dashboard.offersPending} />
                    </div>
                  </section>
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Onboarding & joining</h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <StatCard title="Preboarding" value={dashboard.preboardingPending} />
                      <StatCard title="Onboarding" value={dashboard.onboardingPending} />
                      <StatCard title="Joining this month" value={dashboard.joiningThisMonth} />
                      <StatCard title="Pending requisitions" value={dashboard.pendingRequisitions} />
                      <StatCard title="Pending manpower" value={dashboard.pendingManpowerRequests ?? 0} />
                      <StatCard title="Probation pending" value={dashboard.probationPending} />
                    </div>
                  </section>
                  {analytics && (
                    <section className="space-y-3">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recruitment analytics</h3>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard title="Avg time to hire (days)" value={analytics.averageTimeToHireDays} />
                        <StatCard title="Avg time to fill (days)" value={analytics.averageTimeToFillDays} />
                        <StatCard title="Offer acceptance %" value={`${analytics.offerAcceptanceRate}%`} />
                        <StatCard title="Joining ratio %" value={`${analytics.joiningRatio}%`} />
                        <StatCard title="Onboarding completion %" value={`${analytics.onboardingCompletionRate}%`} />
                        <StatCard title="Probation success %" value={`${analytics.probationSuccessRate}%`} />
                        <StatCard title="Total hired" value={analytics.totalHired} />
                        <StatCard title="Total applied" value={analytics.totalApplied} />
                      </div>
                      {Object.keys(analytics.sourceEffectiveness ?? {}).length > 0 && (
                        <div className="card p-4">
                          <h4 className="mb-2 text-sm font-semibold">Source effectiveness</h4>
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                            {Object.entries(analytics.sourceEffectiveness).map(([source, count]) => (
                              <div key={source} className="flex justify-between rounded border px-3 py-2 text-sm">
                                <span>{source}</span>
                                <span className="font-semibold tabular-nums">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </section>
                  )}
                  {dashboard.hiringFunnel && Object.keys(dashboard.hiringFunnel).length > 0 && (
                    <section className="card space-y-3 p-4">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Pipeline funnel</h3>
                      <p className="text-xs text-muted">
                        Stage counts are workflow states — configure employment types under HR Master data.
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(dashboard.hiringFunnel).map(([stage, count]) => (
                          <div
                            key={stage}
                            className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
                          >
                            <span className="text-slate-600 dark:text-slate-300">{stageLabel(stage)}</span>
                            <span className="font-semibold tabular-nums">{count}</span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted">No dashboard data available.</p>
              )}
            </div>
          )}

          {tab === 'workqueue' && (
            <div className="grid gap-4 lg:grid-cols-2">
              <WorkQueueCard
                title="Pending interviews"
                items={workQueue?.pendingInterviews}
                loading={loading}
                labelFn={(i) => `Interview #${i.id} · candidate ${String((i as { candidateId?: number }).candidateId ?? '—')}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending feedback"
                items={workQueue?.pendingFeedback}
                loading={loading}
                labelFn={(i) => `Interview #${i.id} · candidate ${String((i as { candidateId?: number }).candidateId ?? '—')}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending offers"
                items={workQueue?.pendingOffers}
                loading={loading}
                labelFn={(i) => `Offer #${i.id} · candidate ${String((i as { candidateId?: number }).candidateId ?? '—')}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending preboarding"
                items={workQueue?.pendingPreboarding}
                loading={loading}
                labelFn={(i) => `Session #${i.id} · candidate ${String((i as { candidateId?: number }).candidateId ?? '—')}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending onboarding"
                items={workQueue?.pendingOnboarding}
                loading={loading}
                labelFn={(i) => `Onboarding #${i.id} · candidate ${String((i as { candidateId?: number }).candidateId ?? '—')}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending BGV"
                items={workQueue?.pendingVerifications}
                loading={loading}
                labelFn={(i) => `${(i as { verificationType?: string }).verificationType ?? 'BGV'} #${i.id}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending confirmations"
                items={workQueue?.pendingConfirmations}
                loading={loading}
                labelFn={(i) => `Probation #${i.id} · employee ${String((i as { employeeId?: number }).employeeId ?? '—')}`}
                onOpen={(i) => openCandidateById((i as { candidateId?: number }).candidateId)}
              />
              <WorkQueueCard
                title="Pending manpower"
                items={workQueue?.pendingManpowerRequests}
                loading={loading}
                labelFn={(i) => `${(i as { title?: string }).title ?? 'Request'} #${i.id}`}
                onOpen={() => setTab('planning')}
              />
              <WorkQueueCard
                title="Pending requisitions"
                items={workQueue?.pendingRequisitions}
                loading={loading}
                labelFn={(i) => `${(i as { title?: string }).title ?? 'Requisition'} #${i.id}`}
                onOpen={() => setTab('requisitions')}
              />
            </div>
          )}

          {tab === 'planning' && (
            <DataTable
              columns={manpowerColumns}
              data={manpower}
              loading={loading}
              keyField="id"
              emptyTitle="No manpower requests — create one to plan headcount"
            />
          )}

          {tab === 'requisitions' && (
            <DataTable
              columns={requisitionColumns}
              data={requisitions}
              loading={loading}
              keyField="id"
              emptyTitle="No requisitions — create a position requisition to start hiring"
            />
          )}

          {tab === 'jobs' && (
            <DataTable
              columns={jobColumns}
              data={jobs}
              loading={loading}
              keyField="id"
              emptyTitle="No job postings — approve a requisition and post a job"
            />
          )}

          {tab === 'pipeline' && (
            <div className="space-y-4">
              <FormGrid columns={4} className="items-end">
                <Input
                  title="Search candidates"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name or email…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void loadPipeline(searchQuery);
                  }}
                />
                <Button variant="secondary" className="self-end" onClick={() => void loadPipeline(searchQuery)}>
                  Search
                </Button>
                {searchQuery && (
                  <Button
                    variant="secondary"
                    className="self-end"
                    onClick={() => {
                      setSearchQuery('');
                      void loadPipeline();
                    }}
                  >
                    Clear
                  </Button>
                )}
              </FormGrid>
              <p className="text-xs text-muted">Click a candidate to open the hiring workflow panel.</p>

              {pipeline?.stages?.SEARCH ? (
                <div className="card p-4">
                  <h3 className="mb-3 text-sm font-semibold">{stageLabel('SEARCH')}</h3>
                  {(pipeline.stages.SEARCH ?? []).length === 0 ? (
                    <p className="text-sm text-muted">No candidates match your search.</p>
                  ) : (
                    <CandidateCardList candidates={pipeline.stages.SEARCH} onSelect={setSelectedCandidate} />
                  )}
                </div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {resolvePipelineStages(pipeline?.stageDefinitions).map((stageDef) => {
                    const items = pipeline?.stages?.[stageDef.code] ?? [];
                    const styles = stageColumnStyles(stageDef.color);
                    return (
                      <div
                        key={stageDef.code}
                        className="flex w-[220px] shrink-0 flex-col rounded-xl border-2"
                        style={styles.column}
                      >
                        <div
                          className="flex items-center justify-between border-b px-3 py-2"
                          style={styles.header}
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10"
                              style={{ backgroundColor: stageDef.color }}
                              aria-hidden
                            />
                            <h3 className="truncate text-xs font-semibold text-slate-800 dark:text-slate-100">
                              {stageLabel(stageDef.code, pipeline?.stageDefinitions)}
                            </h3>
                          </div>
                          <Badge variant="default">{items.length}</Badge>
                        </div>
                        <div className="min-h-[120px] flex-1 space-y-2 p-2">
                          {items.length === 0 ? (
                            <p className="px-1 py-2 text-xs text-slate-400">Empty</p>
                          ) : (
                            <CandidateCardList candidates={items} onSelect={setSelectedCandidate} compact />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <RequisitionFormModal
        open={requisitionModalOpen}
        onClose={() => setRequisitionModalOpen(false)}
        onSaved={async () => {
          await loadRequisitions();
          setTab('requisitions');
        }}
      />
      <JobPostingFormModal
        open={jobModalOpen}
        onClose={() => setJobModalOpen(false)}
        onSaved={async () => {
          await loadJobs();
        }}
      />
      <CandidateFormModal
        open={candidateModalOpen}
        onClose={() => setCandidateModalOpen(false)}
        jobOptions={jobOptions}
        onSaved={async () => {
          await loadPipeline();
          setTab('pipeline');
        }}
      />
      <ManpowerFormModal
        open={manpowerModalOpen}
        onClose={() => setManpowerModalOpen(false)}
        onSaved={async () => {
          await loadManpower();
        }}
      />
    </div>
  );
}

function WorkQueueCard({
  title,
  items,
  loading,
  labelFn,
  onOpen,
}: {
  title: string;
  items?: { id: number }[];
  loading: boolean;
  labelFn?: (item: { id: number }) => string;
  onOpen?: (item: { id: number }) => void;
}) {
  const count = items?.length ?? 0;
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h4>
        <Badge variant={count > 0 ? 'warning' : 'default'}>{count}</Badge>
      </div>
      {loading && !items ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : count === 0 ? (
        <p className="text-sm text-muted">Nothing pending</p>
      ) : (
        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-slate-600 dark:text-slate-300">
          {items!.slice(0, 8).map((item) => (
            <li key={item.id}>
              {onOpen ? (
                <button type="button" className="text-left text-primary-600 underline hover:text-primary-800" onClick={() => onOpen(item)}>
                  {labelFn ? labelFn(item) : `#${item.id}`}
                </button>
              ) : (
                labelFn ? labelFn(item) : `#${item.id}`
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CandidateCardList({
  candidates,
  onSelect,
  compact,
}: {
  candidates: HrCandidateSummary[];
  onSelect: (c: HrCandidateSummary) => void;
  compact?: boolean;
}) {
  return (
    <ul className={cn('space-y-2', compact && 'space-y-1.5')}>
      {candidates.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            className={cn(
              'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition hover:border-primary-300 hover:shadow dark:border-slate-600 dark:bg-slate-800 dark:hover:border-primary-600',
              compact && 'px-2 py-1.5',
            )}
            onClick={() => onSelect(c)}
          >
            <div className={cn('font-medium text-slate-900 dark:text-slate-100', compact && 'text-sm')}>{c.fullName}</div>
            <div className="truncate text-xs text-slate-500">{c.email ?? 'No email'}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function RequisitionFormModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [positionsCount, setPositionsCount] = useState('1');
  const [employmentType, setEmploymentType] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setLocation('');
    setPositionsCount('1');
    setEmploymentType('');
    setDescription('');
  }, [open]);

  const save = async () => {
    if (!title.trim() || !employmentType) {
      notify.error('Title and employment type are required');
      return;
    }
    setSaving(true);
    try {
      await hrApi.createRequisition({
        title: title.trim(),
        location: location.trim() || undefined,
        employmentType,
        positionsCount: Number(positionsCount) || 1,
        description: description.trim() || undefined,
      });
      notify.success('Requisition created — submit for approval from the table');
      await onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to create requisition');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New requisition">
      <FormGrid columns={2} className="gap-3">
        <Input label="Position title" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. Sales Executive" />
        <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Chennai" />
        <EmploymentTypeSelect value={employmentType} onChange={setEmploymentType} required />
        <Input
          label="Number of positions"
          type="number"
          min={1}
          value={positionsCount}
          onChange={(e) => setPositionsCount(e.target.value)}
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="col-span-2"
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Create requisition
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function JobPostingFormModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [positionsCount, setPositionsCount] = useState('1');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setLocation('');
    setEmploymentType('');
    setPositionsCount('1');
  }, [open]);

  const save = async () => {
    if (!title.trim() || !employmentType) {
      notify.error('Title and employment type are required');
      return;
    }
    setSaving(true);
    try {
      await hrApi.createJobPosting({
        title: title.trim(),
        location: location.trim() || undefined,
        employmentType,
        positionsCount: Number(positionsCount) || 1,
        statusCode: 'OPEN',
      });
      notify.success('Job posting created');
      await onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to create job posting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New job posting">
      <FormGrid columns={2} className="gap-3">
        <Input label="Job title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
        <EmploymentTypeSelect value={employmentType} onChange={setEmploymentType} required />
        <Input
          label="Positions"
          type="number"
          min={1}
          value={positionsCount}
          onChange={(e) => setPositionsCount(e.target.value)}
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Create posting
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function CandidateFormModal({
  open,
  onClose,
  jobOptions,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  jobOptions: { value: string; label: string }[];
  onSaved: () => Promise<void>;
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobPostingId, setJobPostingId] = useState('');
  const [source, setSource] = useState('INTERNAL');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFullName('');
    setEmail('');
    setPhone('');
    setJobPostingId(jobOptions[0]?.value ?? '');
    setSource('INTERNAL');
  }, [open, jobOptions]);

  const save = async () => {
    if (!fullName.trim()) {
      notify.error('Candidate name is required');
      return;
    }
    setSaving(true);
    try {
      const dup = await hrApi.checkCandidateDuplicates({
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      if (dup.duplicateFound) {
        notify.error(`Possible duplicate: ${dup.matches[0]?.fullName ?? 'existing candidate'}`);
        setSaving(false);
        return;
      }
      await hrApi.addCandidate({
        fullName: fullName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        jobPostingId: jobPostingId ? Number(jobPostingId) : undefined,
        source,
      });
      notify.success('Candidate added');
      await onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to add candidate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add candidate">
      <FormGrid columns={2} className="gap-3">
        <Input label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Select
          label="Job posting"
          value={jobPostingId}
          onChange={setJobPostingId}
          options={[{ value: '', label: 'Unassigned' }, ...jobOptions]}
          placeholder="Select job"
        />
        <Select label="Source" value={source} onChange={setSource} options={CANDIDATE_SOURCES} />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Add candidate
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function ManpowerFormModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [requestType, setRequestType] = useState('NEW_POSITION');
  const [positionsCount, setPositionsCount] = useState('1');
  const [location, setLocation] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setRequestType('NEW_POSITION');
    setPositionsCount('1');
    setLocation('');
    setEmploymentType('');
  }, [open]);

  const save = async () => {
    if (!title.trim()) {
      notify.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const req = await hrApi.createManpowerRequest({
        title: title.trim(),
        requestType,
        positionsCount: Number(positionsCount) || 1,
        location: location.trim() || undefined,
        employmentType: employmentType || undefined,
      });
      await hrApi.submitManpowerRequest(req.id);
      notify.success('Manpower request submitted for approval');
      await onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to create manpower request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New manpower request">
      <FormGrid columns={2} className="gap-3">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. Q3 engineering hires" />
        <Select label="Request type" value={requestType} onChange={setRequestType} options={MANPOWER_REQUEST_TYPES} />
        <Input
          label="Positions"
          type="number"
          min={1}
          value={positionsCount}
          onChange={(e) => setPositionsCount(e.target.value)}
        />
        <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
        <EmploymentTypeSelect value={employmentType} onChange={setEmploymentType} />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Create request
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}
